import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Typography,
  Grid,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  Stepper,
  Step,
  StepLabel,
  Button,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventIcon from '@mui/icons-material/Event';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import DateRangeIcon from '@mui/icons-material/DateRange';
import SourceIcon from '@mui/icons-material/Source';
import Person2Icon from '@mui/icons-material/Person2';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import DirectionsIcon from '@mui/icons-material/Directions';
import DownloadIcon from '@mui/icons-material/Download';
import TimeIcon from '@mui/icons-material/AccessTime';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import CarCrashIcon from '@mui/icons-material/CarCrash';
import NoCrashIcon from '@mui/icons-material/NoCrash';
import MinorCrashIcon from '@mui/icons-material/MinorCrash';
import api from '../../api/api';
import dayjs from 'dayjs';
import ApproveRequestDialogManager from '../commonservices/ApproveRequestDialogManager';
import { useAuth } from '../../contexts/AuthContext';
import ApproveRequestDialogAdmin from '../commonservices/ApproveRequestDialogAdmin';
import * as XLSX from "xlsx";

const PAGE_SIZE = 5;
const CarRequestListingPage = () => {

  var utc = require("dayjs/plugin/utc");
  dayjs.extend(utc);

  const navigate = useNavigate();

  const { user } = useAuth();

  const [carRequests, setCarRequests] = useState([]);

  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    if (user) {
      setUserId(user['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']);
      setRole(user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);
    }
  }, [user]);

  const [filteredData, paginatedData] = useMemo(() => {
    const filteredData = carRequests?.filter(
      req => req.employeeId == userId ||
        (role == "Manager" && req.approvalManagerId == userId) ||
        (role == "Admin" && req.managerApprovedAt)
    );
    return [filteredData, filteredData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)];
  }, [userId, role, page, carRequests]);

  const checkTripTime = (journeyDateStr, arrivalTimeStr) => {

    const journeyDate = dayjs(journeyDateStr).format('YYYY-MM-DD');
    const arrivalTime = dayjs(arrivalTimeStr).format('HH:mm:ss');

    const arrivalDateTime = dayjs(`${journeyDate}T${arrivalTime}`);

    const isArrivalTimeGone = arrivalDateTime.isBefore(dayjs());

    return isArrivalTimeGone;
  }

  const handleApproveOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('approve');
  };

  const handleRejectOpenDialog = (request) => {
    setSelectedRequest(request);
    setDialogOpen('reject');
  };

  const handleCancelRequest = (request) => {

    request.canceledAt = dayjs().utc('z').format();
    request.canceledById = userId;

    api.put(`/api/CarRequests/${request.id}`, request)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const handleCompleteRequest = (request) => {

    request.completeddAt = dayjs().utc('z').format();

    api.put(`/api/CarRequests/${request.id}`, request)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });
  };

  const handleCloseDialog = () => {
    setDialogOpen(null);
    setSelectedRequest(null);
  };


  const handleApproveManager = (remark) => {

    if (dialogOpen === 'approve') {
      selectedRequest.managerApprovedAt = dayjs().utc('z').format();
    } else {
      selectedRequest.managerRejectedAt = dayjs().utc('z').format();
    }

    selectedRequest.managerRemarks = remark;

    api.put(`/api/CarRequests/${selectedRequest.id}`, selectedRequest)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });

  };

  const handleApproveAdmin = (available, selectedCar, driverName, driverMobile) => {

    selectedRequest.adminApprovedAt = dayjs().utc('z').format();
    selectedRequest.vehicleIsNotAvailable = !available;

    if (!available) {
      selectedRequest.adminRemarks = 'Hire a cab or use a self-vehicle.';
    } else {
      selectedRequest.carId = selectedCar;
      selectedRequest.driverName = driverName;
      selectedRequest.driverMobile = driverMobile;
    }

    api.put(`/api/CarRequests/${selectedRequest.id}`, selectedRequest)
      .then(response => {
        loadRequests();
        handleCloseDialog();
      })
      .catch(error => {
        console.error('Error submitting request', error);
        handleCloseDialog();
        // Handle error
      });

  };

  const loadRequests = () => {
    api.get('/api/CarRequests')
      .then(response => {
        setCarRequests(response.data);
      })
      .catch(error => {
        console.error('Error fetching managers', error);
      });
  }

  useEffect(() => {
    loadRequests();
  }, []);

  const handleDownload = async (e) => {

    let wb = XLSX.utils.book_new(),
      pws = XLSX.utils.json_to_sheet([]);

    let carRequestHeading = [[
      "Request Id",
      "Requester",
      "Requested At",
      "Request For",
      "Tentative Km",
      "From Location",
      "To Location",
      "Journey Date",
      "Departure Time",
      "Arrival Time",
      "Number of Person",
      "Purpose of Requirement",
      "Approval Manager",
      "Manager Approved At",
      "Manager Rejected At",
      "Manager Remarks",
      "Admin Approved At",
      "Car",
      "Driver Name",
      "Driver Mobile",
      "Cancelled By",
      "Cancelled At",
      "Completed At"]];

    const carRequestData = filteredData.map((c) => ({
      RequestId: c.requestId,
      Requester: c.employee?.name,
      RequestedAt: dayjs(c.createdAt).format("DD/MM/YYYY hh:mm a"),
      RequestFor: c.requestFor === 0 ? "Self" : c.requestFor === 1 ? `Employee (${c.forEmployee?.name} | ${c.forEmployee?.employeeCode})` : `Guest (${c.forGuestName})`,
      TentativeKm: `${c.tentativeKm} KM`,
      FromLocation: c.fromLocation,
      ToLocation: c.toLocation,
      JourneyDate: dayjs(c.journeyDate).utc('z').local().format("DD/MM/YYYY"),
      DepartureTime: dayjs(c.departureTime).format("hh:mm a"),
      ArrivalTime: dayjs(c.arrivalTime).format("hh:mm a"),
      NumberOfPerson: c.noOfPerson,
      PurposeOfRequirement: c.purposeOfRequirement,
      ApprovalManager: c.approvalManager?.name,
      ManagerApprovedAt: c.managerApprovedAt && dayjs(c.managerApprovedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRejectedAt: c.managerRejectedAt && dayjs(c.managerRejectedAt).format("DD/MM/YYYY hh:mm a"),
      ManagerRemarks: c.managerRemarks,
      AdminApprovedAt: c.adminApprovedAt && dayjs(c.adminApprovedAt).format("DD/MM/YYYY hh:mm a"),
      Car: c.car?.name,
      DriverName: c.driverName,
      DriverMobile: c.driverMobile,
      CanceledBy: c.canceledBy?.name,
      CanceledAt: c.CanceledAt && dayjs(c.CanceledAt).format("DD/MM/YYYY hh:mm a"),
      CompletedAt: c.completeddAt && dayjs(c.completeddAt).format("DD/MM/YYYY hh:mm a")
    }));


    XLSX.utils.sheet_add_aoa(pws, carRequestHeading);
    XLSX.utils.sheet_add_json(pws, carRequestData, { origin: 'A2', skipHeader: true });

    XLSX.utils.book_append_sheet(wb, pws, "Car Requests");

    XLSX.writeFile(wb, "Export.xlsx");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box mb={2} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
        <Typography component="p" variant="h5">Car Requirement</Typography>
        {
          role == "Admin" &&
          <Button size='small' variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Excel
          </Button>
        }
      </Box>
      <Grid container spacing={2}>
        {paginatedData?.map((request) => (
          <Grid item xs={12} key={request.id}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${request.id}-content`}
                id={`panel${request.id}-header`}
              >
                <Box sx={{
                  width: '100%', display: 'grid'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Requester:</b> {request.employee.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SourceIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Request Id:</b> {request.requestId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Journey Date:</b> {dayjs(request.journeyDate).utc('z').local().format("DD/MM/YYYY")} {dayjs(request.departureTime).format("hh:mm a")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MyLocationIcon sx={{ mr: 1 }} />
                    <Typography variant="body1"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal'
                      }}
                    >
                      <b>From:</b> {request.fromLocation}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon sx={{ mr: 1 }} />
                    <Typography variant="body1"
                      sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        WebkitLineClamp: 2,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal'
                      }}
                    >
                      <b>To:</b> {request.toLocation}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person2Icon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      <b>Manager:</b> {request.approvalManager.name}
                    </Typography>
                  </Box>
                  {request.canceledBy &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color='warning' sx={{ mr: 1 }} />
                      <Typography variant="body1">
                        <b>Cancelled By:</b> {request.canceledBy.name}
                      </Typography>
                    </Box>
                  }
                  <Box sx={{ width: '100%', overflowX: 'auto', display: 'block' }}>
                    <Stepper activeStep={request.adminApprovedAt || request.adminRejectedAt ? 3 : request.managerApprovedAt ? 2 : 1}>
                      <Step key={'request'} >
                        <StepLabel>Request</StepLabel>
                      </Step>
                      <Step key={'manager'}>
                        <StepLabel {...{ error: (request.managerRejectedAt != null) }}>Manager</StepLabel>
                      </Step>
                      <Step key={'admin'}>
                        <StepLabel {...{ error: (request.adminRejectedAt != null) }}>Admin</StepLabel>
                      </Step>
                    </Stepper>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Request For:</b> {request.requestFor === 0 ? "Self" : request.requestFor === 1 ? `Employee (${request.forEmployee?.name} | ${request.forEmployee?.employeeCode})` : `Guest (${request.forGuestName})`}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DirectionsIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Tentative KM:</b> {request.tentativeKm} km</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TimeIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Journey Time:</b> {dayjs(request.departureTime).format("hh:mm a")} - {dayjs(request.arrivalTime).format("hh:mm a")}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>No. of Persons:</b> {request.noOfPerson}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Purpose of Requirement:</b> {request.purposeOfRequirement}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DateRangeIcon sx={{ mr: 1 }} />
                    <Typography variant="body2"><b>Requested At:</b> {dayjs(request.createdAt).format("DD/MM/YYYY hh:mm a")}</Typography>
                  </Box>
                  {request.managerRemarks &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {request.managerApprovedAt ? <GppGoodIcon color='success' sx={{ mr: 1 }} /> : <GppBadIcon color='error' sx={{ mr: 1 }} />}
                      <Typography variant="body2"><b>Manager Remark:</b> {request.managerRemarks}</Typography>
                    </Box>
                  }
                  {request.vehicleIsNotAvailable != null &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {request.car ? <NoCrashIcon color='success' sx={{ mr: 1 }} /> : <CarCrashIcon color='error' sx={{ mr: 1 }} />}
                      {request.car ?
                        <Typography variant="body2"><b>Car:</b> {request.car.name}</Typography> :
                        <Typography variant="body2"><b>Message:</b> Hire a cab or use a self-vehicle.</Typography>
                      }
                    </Box>
                  }
                  {request.driverName &&
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AirlineSeatReclineNormalIcon sx={{ mr: 1 }} />
                      <Typography variant="body2"><b>Driver:</b> {request.driverName} | {request.driverMobile}</Typography>
                    </Box>
                  }
                  {
                    request.completeddAt == null &&
                    <>
                      {((role == "Admin" && !request.adminApprovedAt && !request.adminRejectedAt) 
                        || (role == "Manager" && !request.managerApprovedAt && !request.managerRejectedAt)) 
                        &&
                        <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, }}>
                          <Button onClick={() => handleApproveOpenDialog(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                            Approve
                          </Button>
                          {role != "Admin" 
                            && 
                            <Button onClick={() => handleRejectOpenDialog(request)} variant='contained' color='error' sx={{ flex: '1 1 auto' }} >
                              Reject
                            </Button>
                          }
                        </Box>
                      }
                      <Box sx={{ width: '100%', display: 'flex', gap: 2, mb: 1, }}>
                        {role != "Admin" && !request.managerRejectedAt && !request.canceledBy 
                          && 
                            <Button fullWidth onClick={() => handleCancelRequest(request)} variant='contained' color='warning' sx={{ flex: '1 1 auto' }} >
                              Cancel Request
                            </Button>
                        }
                        {role == "Requester" && !request.managerRejectedAt && !request.canceledBy && checkTripTime(request.journeyDate, request.arrivalTime) 
                          && 
                          <Button fullWidth onClick={() => handleCompleteRequest(request)} variant='contained' color='success' sx={{ flex: '1 1 auto' }} >
                            Complete Trip
                          </Button>
                        }
                      </Box>
                    </>
                  }

                </Box>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 6 }}>
        <Pagination
          count={Math.ceil(filteredData.length / PAGE_SIZE)}
          page={page}
          siblingCount={1}
          boundaryCount={1}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      <Fab color="primary" aria-label="add" sx={{ position: 'fixed', bottom: '2rem', right: '2rem' }} onClick={() => navigate('/car-request/add')}>
        <AddIcon />
      </Fab>
      {role == "Manager" &&
        <ApproveRequestDialogManager open={dialogOpen != null} isApprove={dialogOpen === 'approve'} handleClose={handleCloseDialog} handleApprove={handleApproveManager} />
      }
      {role == "Admin" &&
        <ApproveRequestDialogAdmin open={dialogOpen != null} handleClose={handleCloseDialog} handleApprove={handleApproveAdmin} />
      }
    </Box>
  );
};

export default CarRequestListingPage;
